import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Check, ChevronsUpDown } from 'lucide-react';
import { BILLING_ROUTES } from '@/api/constants/app.url';
import { usePlan } from '@/modules/auth';
import { cn } from '@/shared/utils/cn';
import { resolveRoleLabel } from '@/shared/utils/dashboard-role';
import { useWorkspaces, useSwitchWorkspace, type Workspace } from '@/modules/workspace';

interface Props {
    tenantName: string;
    showPlan:   boolean;
}

export function WorkspaceCard({ tenantName, showPlan }: Props) {
    const plan = usePlan();
    const { data: workspaces } = useWorkspaces();
    const switchWorkspace = useSwitchWorkspace();

    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    // Close the switcher on any outside click. Mirrors the topbar user menu.
    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    // Only a user with 2+ workspaces gets a switcher; otherwise the card is the
    // same static label it always was.
    const canSwitch = (workspaces?.length ?? 0) > 1;
    const isSwitching = switchWorkspace.isPending;

    function onSelect(w: Workspace) {
        setOpen(false);
        if (w.isCurrent || isSwitching) return;
        switchWorkspace.mutate(w.tenantId);
    }

    return (
        <div className="relative px-3 pb-2 pt-4" ref={rootRef}>
            <div className="rounded-lg border border-brand-navy-100 bg-brand-navy-50 px-3 py-2.5">
                <div className="font-ui text-[10px] font-semibold uppercase tracking-wider text-brand-navy-300">
                    Workspace
                </div>

                {canSwitch ? (
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        disabled={isSwitching}
                        className="mt-0.5 flex w-full items-center gap-1.5 text-left disabled:opacity-60"
                        aria-haspopup="listbox"
                        aria-expanded={open}
                    >
                        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-brand-navy">
                            {isSwitching ? 'Switching…' : tenantName}
                        </span>
                        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-brand-navy-300" />
                    </button>
                ) : (
                    <div className="mt-0.5 truncate text-sm font-semibold text-brand-navy">{tenantName}</div>
                )}

                {showPlan && (
                    <NavLink
                        to={BILLING_ROUTES.root}
                        className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-brand-sky-50 px-1.5 py-0.5 font-ui text-[10px] font-semibold text-brand-sky-500 hover:bg-brand-sky-100"
                    >
                        {plan?.name ?? 'Free'} plan
                    </NavLink>
                )}
            </div>

            {open && canSwitch && (
                <div
                    role="listbox"
                    className="absolute inset-x-3 top-full z-40 mt-1 overflow-hidden rounded-xl2 border border-ink-100 bg-white py-1 shadow-pop"
                >
                    <div className="px-3 pb-1 pt-1.5 font-ui text-[10px] font-semibold uppercase tracking-wider text-ink-300">
                        Switch workspace
                    </div>
                    {workspaces!.map((w) => {
                        const roleLabel = resolveRoleLabel(w.role, w.subtype);
                        return (
                            <button
                                key={w.tenantId}
                                type="button"
                                role="option"
                                aria-selected={w.isCurrent}
                                onClick={() => onSelect(w)}
                                disabled={isSwitching}
                                className={cn(
                                    'flex w-full items-center gap-2 px-3 py-2 text-left transition hover:bg-ink-50 disabled:cursor-not-allowed',
                                    w.isCurrent && 'bg-brand-navy-50',
                                )}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-semibold text-brand-navy">{w.tenantName}</div>
                                    <div className="font-ui text-[10px] text-ink-500">{roleLabel}</div>
                                </div>
                                {w.isCurrent && <Check className="h-4 w-4 shrink-0 text-brand-sky-500" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
